"use client";

import { motion, type Variants } from "motion/react";

import { cn } from "@/lib/utils";

// =============================================================================

const letterVariants: Variants = {
  rest: { y: "0%" },
  hover: { y: "-100%" },
};

type TextRollProps = {
  children: string;
  className?: string;
  /** Delay between two letters, in seconds */
  stagger?: number;
};

/**
 * Letters roll up on hover, revealing a copy of the text from below.
 * Must be rendered inside an element with `initial="rest" whileHover="hover"`
 * (or animate="hover" to force it), e.g. a motion link.
 */
export function TextRoll({
  children,
  className,
  stagger = 0.018,
}: TextRollProps) {
  const letters = Array.from(children);

  return (
    <span className={cn("relative inline-flex overflow-hidden", className)}>
      <span className="sr-only">{children}</span>
      <span aria-hidden className="inline-flex">
        {letters.map((letter, index) => (
          <motion.span
            key={`${letter}-${index}`}
            variants={letterVariants}
            transition={{
              duration: 0.45,
              ease: [0.65, 0, 0.35, 1],
              delay: index * stagger,
            }}
            className="relative inline-block whitespace-pre"
          >
            {letter}
            <span className="absolute top-full left-0">{letter}</span>
          </motion.span>
        ))}
      </span>
    </span>
  );
}

// =============================================================================
